import React, { ReactNode, useEffect, useReducer, useCallback } from "react";
import AuthContext from "./AuthContext";
import { tokenStorage } from "../../utils/auth/tokenStorage";
import { vanillaClient } from "../../lib/api/trpc";
import tokenRefreshService from "../../services/tokenRefreshService";
import {
  AuthState,
  LoginCredentials,
  MfaCredentials,
  ResetPasswordCredentials,
  ChangePasswordCredentials,
  User,
} from "../../types/auth";
import config from "../../config/environment";
import { logError } from "../../utils/errors/errorHandler";

// Helper function to map API user to frontend User type
const mapApiUserToUser = (apiUser: any): User => {
  return {
    id: apiUser.id,
    email: apiUser.email,
    name:
      `${apiUser.firstName || ""} ${apiUser.lastName || ""}`.trim() ||
      apiUser.email,
    role: apiUser.role,
    status: apiUser.accountStatus || apiUser.status,
    forcePasswordChange: apiUser.forcePasswordChange,
    firstName: apiUser.firstName,
    lastName: apiUser.lastName,
    accountStatus: apiUser.accountStatus,
    mfaEnabled: apiUser.mfaEnabled,
    mfaType: apiUser.mfaType,
    isActive: apiUser.isActive,
    createdAt: apiUser.createdAt,
    updatedAt: apiUser.updatedAt,
    lastLoginAt: apiUser.lastLoginAt,
    lastPasswordChange: apiUser.lastPasswordChange,
    lastFailedLoginAttempt: apiUser.lastFailedLoginAttempt,
    pendingEmail: apiUser.pendingEmail,
  };
};

/**
 * Enhanced AuthProvider with real API integration
 *
 * Features:
 * - Uses real tRPC API endpoints
 * - Optimized with memoized functions
 * - Improved MFA handling with session expiration
 * - Automatic token refresh mechanism
 * - Centralized error handling
 */

// Auth action types
type AuthAction =
  | {
      type: "INITIALIZE";
      payload: { user: User | null; isAuthenticated: boolean };
    }
  | { type: "LOGIN_REQUEST" }
  | { type: "LOGIN_SUCCESS"; payload: { user: User } }
  | { type: "LOGIN_FAILURE"; payload: string }
  | { type: "MFA_REQUIRED"; payload: { email: string } }
  | { type: "MFA_SUCCESS"; payload: { user: User } }
  | { type: "MFA_FAILURE"; payload: string }
  | { type: "MFA_RESEND"; payload: { email: string } }
  | { type: "LOGOUT" }
  | { type: "RESET_PASSWORD_REQUEST" }
  | { type: "RESET_PASSWORD_SUCCESS" }
  | { type: "RESET_PASSWORD_FAILURE"; payload: string }
  | { type: "CLEAR_ERROR" };

// Initial auth state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  isInitialized: false,
  requiresMfa: false,
  mfaEmail: null,
  mfaSessionExpiry: null,
  error: null,
};

// Auth reducer
const reducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case "INITIALIZE":
      return {
        ...state,
        isAuthenticated: action.payload.isAuthenticated,
        user: action.payload.user,
        isLoading: false,
        isInitialized: true,
      };
    case "LOGIN_REQUEST":
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case "LOGIN_SUCCESS":
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        isLoading: false,
        requiresMfa: false,
        mfaEmail: null,
        mfaSessionExpiry: null,
        error: null,
      };
    case "LOGIN_FAILURE":
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };
    case "MFA_REQUIRED":
      return {
        ...state,
        isLoading: false,
        requiresMfa: true,
        mfaEmail: action.payload.email,
        mfaSessionExpiry: Date.now() + config.auth.mfaSessionDuration,
        error: null,
      };
    case "MFA_RESEND":
      return {
        ...state,
        mfaEmail: action.payload.email,
        mfaSessionExpiry: Date.now() + config.auth.mfaSessionDuration,
        error: null,
      };
    case "MFA_SUCCESS":
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        isLoading: false,
        requiresMfa: false,
        mfaEmail: null,
        mfaSessionExpiry: null,
        error: null,
      };
    case "MFA_FAILURE":
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };
    case "LOGOUT":
      return {
        ...initialState,
        isLoading: false,
        isInitialized: true,
      };
    case "RESET_PASSWORD_REQUEST":
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case "RESET_PASSWORD_SUCCESS":
      return {
        ...state,
        isLoading: false,
        error: null,
      };
    case "RESET_PASSWORD_FAILURE":
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };
    case "CLEAR_ERROR":
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  console.log("AuthProvider - Current state:", state);

  // Check for MFA session expiration
  useEffect(() => {
    if (state.requiresMfa && state.mfaSessionExpiry) {
      const checkExpiryInterval = setInterval(() => {
        if (Date.now() > state.mfaSessionExpiry!) {
          // MFA session has expired, reset the state
          dispatch({ type: "LOGOUT" });
        }
      }, 10000); // Check every 10 seconds

      return () => clearInterval(checkExpiryInterval);
    }
  }, [state.requiresMfa, state.mfaSessionExpiry]);

  // Initialize auth state
  const initialize = useCallback(async () => {
    try {
      const token = tokenStorage.getAuthToken();

      if (token) {
        try {
          // Call the API to get the current user info
          // @ts-ignore - This is a workaround for TypeScript errors with tRPC
          const userResponse = await vanillaClient.auth.me.query();

          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: true,
              user: mapApiUserToUser(userResponse),
            },
          });
        } catch (error) {
          console.error("Auth initialization error:", error);
          logError(error, "Auth initialization");
          // If we can't get the user info, clear tokens and initialize as not authenticated
          tokenStorage.clearTokens();
          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } else {
        console.log("No auth token found, initializing as unauthenticated");
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (error) {
      console.error("Auth initialization catch-all error:", error);
      logError(error, "Auth initialization");
      dispatch({
        type: "INITIALIZE",
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // Start/stop token refresh service based on authentication state
  useEffect(() => {
    if (state.isAuthenticated && !state.requiresMfa) {
      // Start the token refresh service when authenticated
      tokenRefreshService.start();
    } else {
      // Stop the service when not authenticated
      tokenRefreshService.stop();
    }

    // Cleanup on unmount
    return () => {
      tokenRefreshService.stop();
    };
  }, [state.isAuthenticated, state.requiresMfa]);

  // Login function
  const login = useCallback(
    async (credentials: LoginCredentials): Promise<void> => {
      console.log(
        "AuthProvider login function called with:",
        credentials.email,
      );
      dispatch({ type: "LOGIN_REQUEST" });

      try {
        console.log("Making tRPC auth.login.mutate call...");

        let response;
        try {
          // Call the API to login using the vanilla tRPC client
          // @ts-ignore - This is a workaround for TypeScript errors with tRPC
          response = await vanillaClient.auth.login.mutate(credentials);
          console.log("Login API response:", response);
        } catch (trpcError: any) {
          console.log("tRPC error caught:", trpcError);

          // For tRPC errors, check if it's an MFA requirement
          // Deeply inspect the error structure
          console.log(
            "Full tRPC error structure:",
            JSON.stringify(trpcError, null, 2),
          );

          // Handle common error formats
          const errorData =
            trpcError?.data?.data || trpcError?.shape?.data?.data || {};
          const errorCode = errorData.code || "";
          const errorMessage = trpcError?.message || errorData?.message || "";

          // Handle array format from the response you provided
          let isMfaRequired = false;

          // Check for MFA required in various locations in the error structure
          if (
            errorMessage.includes("MFA verification required") ||
            errorCode === "PRECONDITION_FAILED"
          ) {
            isMfaRequired = true;
          }
          // Check for error.data.code === PRECONDITION_FAILED pattern
          else if (trpcError?.error?.data?.code === "PRECONDITION_FAILED") {
            isMfaRequired = true;
          }
          // Check array format from the example
          else if (Array.isArray(trpcError)) {
            for (const item of trpcError) {
              if (
                item?.error?.message?.includes("MFA verification required") ||
                item?.error?.data?.code === "PRECONDITION_FAILED"
              ) {
                isMfaRequired = true;
                break;
              }
            }
          }
          // Check if the error is a string that can be parsed as JSON (sometimes happens with tRPC)
          else if (typeof trpcError === "string") {
            try {
              const parsedError = JSON.parse(trpcError);
              if (
                Array.isArray(parsedError) &&
                parsedError[0]?.error?.data?.code === "PRECONDITION_FAILED"
              ) {
                isMfaRequired = true;
              }
            } catch (e) {
              // Not parseable JSON, continue
            }
          }

          if (isMfaRequired) {
            console.log("MFA required from tRPC error");

            // Just dispatch the MFA required action - let the UI handle navigation
            dispatch({
              type: "MFA_REQUIRED",
              payload: { email: credentials.email },
            });
            return;
          }

          // Re-throw if not MFA related
          throw trpcError;
        }

        // Check if MFA is required from successful response
        if (response.requiresMfa) {
          console.log("MFA required from API response");
          dispatch({
            type: "MFA_REQUIRED",
            payload: { email: credentials.email },
          });
          return;
        }

        console.log("Storing tokens");
        // Store tokens - adjusting for the API response structure
        // The server returns tokens in a nested structure
        if (response.tokens) {
          tokenStorage.setAuthToken(response.tokens.accessToken);
          if (response.tokens.refreshToken) {
            tokenStorage.setRefreshToken(response.tokens.refreshToken);
          }
        } else if (response.token) {
          // Fallback for backward compatibility
          tokenStorage.setAuthToken(response.token);
          if (response.refreshToken) {
            tokenStorage.setRefreshToken(response.refreshToken);
          }
        }

        console.log("Dispatching LOGIN_SUCCESS");
        dispatch({
          type: "LOGIN_SUCCESS",
          payload: { user: mapApiUserToUser(response.user) },
        });
      } catch (error: any) {
        console.error("Login API error:", error);
        console.error("Error type:", typeof error);
        console.error("Error structure:", JSON.stringify(error, null, 2));
        logError(error, "Login");

        // Enhanced MFA error detection
        const errorMessage = error?.message || "";
        const errorData = error?.data || error?.shape?.data;
        const errorCode = error?.code || error?.shape?.code;

        console.log("Error message:", errorMessage);
        console.log("Error data:", errorData);
        console.log("Error code:", errorCode);

        // Check multiple paths where MFA verification might be indicated
        if (
          errorMessage.includes("MFA verification required") ||
          errorData?.message?.includes("MFA verification required") ||
          errorCode === "PRECONDITION_FAILED" ||
          errorData?.code === "PRECONDITION_FAILED"
        ) {
          console.log("MFA required detected, dispatching MFA_REQUIRED action");
          dispatch({
            type: "MFA_REQUIRED",
            payload: { email: credentials.email },
          });
          return;
        }

        dispatch({
          type: "LOGIN_FAILURE",
          payload: error?.message || "Login failed",
        });
      }
    },
    [],
  );

  // MFA verification
  const verifyMfa = useCallback(
    async (credentials: MfaCredentials): Promise<void> => {
      console.log(
        "AuthProvider verifyMfa called with credentials:",
        credentials,
      );
      dispatch({ type: "LOGIN_REQUEST" });

      try {
        // Check if MFA session has expired
        if (state.mfaSessionExpiry && Date.now() > state.mfaSessionExpiry) {
          dispatch({
            type: "MFA_FAILURE",
            payload: "MFA session has expired. Please login again.",
          });
          return;
        }

        console.log(
          "Making tRPC auth.verifyMfa.mutate call with:",
          credentials,
        );
        // Call the API to verify MFA
        // @ts-ignore - This is a workaround for TypeScript errors with tRPC
        const response = await vanillaClient.auth.verifyMfa.mutate(credentials);

        // Store tokens - adjusting for the API response structure
        if (response.tokens) {
          tokenStorage.setAuthToken(response.tokens.accessToken);
          if (response.tokens.refreshToken) {
            tokenStorage.setRefreshToken(response.tokens.refreshToken);
          }
        } else if (response.token) {
          // Fallback for backward compatibility
          tokenStorage.setAuthToken(response.token);
          if (response.refreshToken) {
            tokenStorage.setRefreshToken(response.refreshToken);
          }
        }

        dispatch({
          type: "MFA_SUCCESS",
          payload: { user: mapApiUserToUser(response.user) },
        });
      } catch (error: any) {
        logError(error, "MFA Verification");
        dispatch({
          type: "MFA_FAILURE",
          payload: error?.message || "MFA verification failed",
        });
      }
    },
    [state.mfaSessionExpiry],
  );

  // Resend MFA code
  const resendMfaCode = useCallback(async (email: string): Promise<void> => {
    try {
      // Call the API to resend MFA code
      // @ts-ignore - This is a workaround for TypeScript errors with tRPC
      await vanillaClient.auth.resendMfaCode.mutate({ email });

      dispatch({
        type: "MFA_RESEND",
        payload: { email },
      });
    } catch (error: any) {
      logError(error, "Resend MFA Code");
      dispatch({
        type: "MFA_FAILURE",
        payload: error?.message || "Failed to resend MFA code",
      });
    }
  }, []);

  // Logout function
  const logout = useCallback(async (): Promise<void> => {
    try {
      // Call the API to logout
      // @ts-ignore - This is a workaround for TypeScript errors with tRPC
      await vanillaClient.auth.logout.mutate();
    } catch (error) {
      logError(error, "Logout");
    } finally {
      // Always clear tokens and log out locally, even if the API call fails
      tokenStorage.clearTokens();
      dispatch({ type: "LOGOUT" });
    }
  }, []);

  // Listen for auth expiration events from API client
  useEffect(() => {
    const handleAuthExpired = () => {
      console.log("Auth expired event received");
      logout();
    };

    window.addEventListener("auth:expired", handleAuthExpired);

    return () => {
      window.removeEventListener("auth:expired", handleAuthExpired);
    };
  }, [logout]);

  // Reset password request
  const resetPasswordRequest = useCallback(
    async (email: string): Promise<void> => {
      dispatch({ type: "RESET_PASSWORD_REQUEST" });

      try {
        // Call the API to request password reset
        // @ts-ignore - This is a workaround for TypeScript errors with tRPC
        await vanillaClient.auth.requestPasswordReset.mutate({ email });

        dispatch({ type: "RESET_PASSWORD_SUCCESS" });
      } catch (error: any) {
        logError(error, "Reset Password Request");
        dispatch({
          type: "RESET_PASSWORD_FAILURE",
          payload: error?.message || "Reset password request failed",
        });
      }
    },
    [],
  );

  // Reset password
  const resetPassword = useCallback(
    async (credentials: ResetPasswordCredentials): Promise<void> => {
      dispatch({ type: "RESET_PASSWORD_REQUEST" });

      try {
        // Call the API to reset password
        // @ts-ignore - This is a workaround for TypeScript errors with tRPC
        await vanillaClient.auth.resetPassword.mutate(credentials);

        dispatch({ type: "RESET_PASSWORD_SUCCESS" });
      } catch (error: any) {
        logError(error, "Reset Password");
        dispatch({
          type: "RESET_PASSWORD_FAILURE",
          payload: error?.message || "Reset password failed",
        });
      }
    },
    [],
  );

  // Change password
  const changePassword = useCallback(
    async (credentials: ChangePasswordCredentials): Promise<void> => {
      dispatch({ type: "RESET_PASSWORD_REQUEST" });

      try {
        // Call the API to change password
        // @ts-ignore - This is a workaround for TypeScript errors with tRPC
        await vanillaClient.auth.changePassword.mutate({
          currentPassword: credentials.currentPassword,
          newPassword: credentials.newPassword,
        });

        dispatch({ type: "RESET_PASSWORD_SUCCESS" });
      } catch (error: any) {
        logError(error, "Change Password");
        dispatch({
          type: "RESET_PASSWORD_FAILURE",
          payload: error?.message || "Change password failed",
        });
      }
    },
    [],
  );

  // Clear error state
  const clearError = useCallback(() => {
    dispatch({ type: "CLEAR_ERROR" });
  }, []);

  // Refresh current user data
  const refreshUser = useCallback(async (): Promise<void> => {
    try {
      const token = tokenStorage.getAuthToken();

      if (token && state.isAuthenticated) {
        // Call the API to get the current user info
        // @ts-ignore - This is a workaround for TypeScript errors with tRPC
        const userResponse = await vanillaClient.auth.me.query();

        dispatch({
          type: "LOGIN_SUCCESS",
          payload: { user: mapApiUserToUser(userResponse) },
        });
      }
    } catch (error) {
      console.error("Failed to refresh user data:", error);
      logError(error, "Refresh user");
    }
  }, [state.isAuthenticated]);

  // Context value with memoized functions
  const contextValue = {
    ...state,
    login,
    verifyMfa,
    resendMfaCode,
    logout,
    resetPasswordRequest,
    resetPassword,
    changePassword,
    clearError,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
